import React, { useState, useCallback, useEffect } from 'react';
import { ParametersLayout } from './ParametersLayout';
import { ui } from '../../../config/uiSizes';

interface DivisionMappingRow {
  id: string;
  sourceDivision: string;
  targetDivision: string;
  [key: string]: unknown;
}

interface DivisionMappingParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const DivisionMappingParameters: React.FC<DivisionMappingParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [rows, setRows] = useState<DivisionMappingRow[]>(() => {
    const divisionMap = parameters?.['divisionMap'];

    if (divisionMap && typeof divisionMap === 'object') {
      if (Array.isArray(divisionMap)) {
        return divisionMap.length > 0
          ? divisionMap
          : [{ id: '1', sourceDivision: '', targetDivision: '' }];
      } else {
        const entries = Object.entries(divisionMap as Record<string, string>);
        if (entries.length > 0) {
          return entries.map(([sourceDivision, targetDivision], index) => ({
            id: (index + 1).toString(),
            sourceDivision,
            targetDivision
          }));
        }
      }
    }

    return [{ id: '1', sourceDivision: '', targetDivision: '' }];
  });

  const [defaultDivision, setDefaultDivision] = useState<string>(
    (parameters?.['defaultDivision'] as string) || ''
  );
  const [updateReferences, setUpdateReferences] = useState<boolean>(
    parameters?.['updateReferences'] !== undefined
      ? (parameters['updateReferences'] as boolean)
      : true
  );

  useEffect(() => {
    if (onParametersChange) {
      const divisionMapObject = rows.reduce<Record<string, string>>((acc, row) => {
        if (row.sourceDivision && row.targetDivision) {
          acc[row.sourceDivision] = row.targetDivision;
        }
        return acc;
      }, {});

      const params: Record<string, unknown> = {
        divisionMap: divisionMapObject,
        updateReferences
      };

      if (defaultDivision) {
        params['defaultDivision'] = defaultDivision;
      }

      onParametersChange(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, defaultDivision, updateReferences]);

  const handleAddRow = useCallback(() => {
    const newRow: DivisionMappingRow = {
      id: Date.now().toString(),
      sourceDivision: '',
      targetDivision: ''
    };
    setRows((prev) => [...prev, newRow]);
  }, []);

  const handleRemoveRow = useCallback((id: string | number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const handleUpdateRow = useCallback(
    (id: string | number, field: 'sourceDivision' | 'targetDivision', value: string) => {
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    },
    []
  );

  const renderRow = useCallback(
    (row: DivisionMappingRow, index: number, onRemove: () => void) => (
      <div className={`flex items-end ${ui.gap.md}`}>
        <div className="flex-1">
          {index === 0 && (
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Source Division ID
            </label>
          )}
          <input
            type="text"
            value={row.sourceDivision}
            onChange={(e) => handleUpdateRow(row.id, 'sourceDivision', e.target.value)}
            placeholder="source-division-id-1"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        <div className="flex items-center pb-2">
          <svg
            className={`${ui.svg.sm} text-gray-400 dark:text-gray-500`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </div>

        <div className="flex-1">
          {index === 0 && (
            <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
              Target Division ID
            </label>
          )}
          <input
            type="text"
            value={row.targetDivision}
            onChange={(e) => handleUpdateRow(row.id, 'targetDivision', e.target.value)}
            placeholder="target-division-id-1"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        <button
          type="button"
          onClick={onRemove}
          className={`flex items-center gap-2 ${ui.input.base} ${ui.text.label} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mb-0.5`}
        >
          <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Remove
        </button>
      </div>
    ),
    [handleUpdateRow]
  );

  return (
    <div className="space-y-4">
      <ParametersLayout<DivisionMappingRow>
        title="Division Mapping"
        description="Map division IDs from source organization to target organization."
        addButtonLabel="Add division mapping"
        rows={rows}
        onAddRow={handleAddRow}
        onRemoveRow={handleRemoveRow}
        renderRow={renderRow}
      />

      {/* Additional Options */}
      <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding} space-y-4`}>
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Default Division (Optional)
          </label>
          <input
            type="text"
            value={defaultDivision}
            onChange={(e) => setDefaultDivision(e.target.value)}
            placeholder="default-target-division-id"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
          <p className={`${ui.text.label} text-gray-500 dark:text-gray-400 mt-1`}>
            Division ID to use when no mapping exists
          </p>
        </div>

        <div className={`flex items-center ${ui.gap.sm}`}>
          <input
            type="checkbox"
            id="updateReferences"
            checked={updateReferences}
            onChange={(e) => setUpdateReferences(e.target.checked)}
            className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
          />
          <label htmlFor="updateReferences" className={`${ui.text.label} text-gray-700 dark:text-gray-300`}>
            Update division references in properties
          </label>
        </div>
      </div>
    </div>
  );
};

export default DivisionMappingParameters;
